import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import { handleError } from 'redux/saga/utils/errorUtils';
import {
  downloadCallRecording,
  downloadCallRecordingFailure,
  downloadCallRecordingSuccess,
  fetchAllCallRecordings,
  fetchAllCallRecordingsFailure,
  fetchAllCallRecordingsSuccess,
  fetchCallRecordingSummary,
  fetchCallRecordingSummaryFailure,
  fetchCallRecordingSummarySuccess,
  fetchCallRecordingsByMonth,
  fetchCallRecordingsByMonthFailure,
  fetchCallRecordingsByMonthSuccess,
  fetchDistinctYears,
  fetchDistinctYearsFailure,
  fetchDistinctYearsSuccess,
  setShowSuccessModal,
  uploadCallRecordings,
  uploadCallRecordingsFailure,
  uploadCallRecordingsSuccess,
} from 'redux/slice/compliance/recording/Recording.slice';
import {
  IGetAllCallRecordingsRequest,
  IGetAllCallRecordingsResponse,
  IGetCallRecordingsByMonthRequest,
  IGetCallRecordingsByMonthResponse,
  IGetCallRecordingsRequest,
  IGetCallRecordingsResponse,
  IGetDistinctYearsRequest,
  IGetDistinctYearsResponse,
  IUploadCallRecordingsRequest,
  IUploadCallRecordingsResponse,
} from 'redux/types/compliance/Recording.types';
import { complianceEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

function* handleFetchCallRecordingSummarySaga(
  action: PayloadAction<IGetCallRecordingsRequest>
) {
  try {
    const { allocatorId, uploadMonth = 1, uploadYear } = action.payload;
    const endpoint = complianceEndPoints.GET_CALL_RECORDINGS(
      allocatorId,
      uploadMonth,
      uploadYear
    );
    const response: AxiosResponse<IGetCallRecordingsResponse> =
      yield HTTPClient.GetRequest(endpoint);
    yield put(fetchCallRecordingSummarySuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchCallRecordingSummaryFailure, '');
  }
}

function* handleFetchAllCallRecordingsSaga(
  action: PayloadAction<IGetAllCallRecordingsRequest>
) {
  try {
    const {
      allocatorId,
      agencyId,
      uploadMonth = 1,
      uploadYear,
    } = action.payload;
    const endpoint = complianceEndPoints.GET_CALL_RECORDINGS_BY_AGENCY(
      allocatorId,
      agencyId,
      uploadMonth,
      uploadYear
    );
    const response: AxiosResponse<IGetAllCallRecordingsResponse> =
      yield HTTPClient.GetRequest(endpoint);
    yield put(fetchAllCallRecordingsSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchAllCallRecordingsFailure, '');
  }
}

function* handleDownloadCallRecordingSaga(
  action: PayloadAction<{ recordingId: string }>
) {
  try {
    const { recordingId } = action.payload;
    const endpoint = complianceEndPoints.DOWNLOAD_CALL_RECORDING(recordingId);
    const response: AxiosResponse<{ message: string; data: string }> =
      yield HTTPClient.GetRequest(endpoint);
    yield put(downloadCallRecordingSuccess(response.data));
    window.open(response.data.data, '_blank');
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, downloadCallRecordingFailure, '');
  }
}

function* handleFetchCallRecordingsByMonthSaga(
  action: PayloadAction<IGetCallRecordingsByMonthRequest>
) {
  try {
    const { allocatorId, agencyId, uploadYear } = action.payload;
    const endpoint = complianceEndPoints.GET_CALL_RECORDINGS_BY_MONTH(
      allocatorId,
      agencyId,
      uploadYear
    );
    const response: AxiosResponse<IGetCallRecordingsByMonthResponse> =
      yield HTTPClient.GetRequest(endpoint);
    yield put(fetchCallRecordingsByMonthSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchCallRecordingsByMonthFailure, '');
  }
}

function* handleUploadCallRecordingsSaga(
  action: PayloadAction<IUploadCallRecordingsRequest>
) {
  try {
    const { allocatorId, agencyId, uploadYear, uploadMonth, callRecordings } =
      action.payload;
    const formdata = new FormData();
    formdata.append('agencyId', agencyId);
    formdata.append('allocatorId', allocatorId);
    formdata.append('uploadYear', uploadYear);
    formdata.append('uploadMonth', uploadMonth);
    callRecordings.forEach((file) => {
      formdata.append(`callRecordings`, file);
    });
    const endpoint = complianceEndPoints.UPLOAD_CALL_RECORDINGS();
    const response: AxiosResponse<IUploadCallRecordingsResponse> =
      yield HTTPClient.PostRequest(endpoint, formdata, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    yield put(uploadCallRecordingsSuccess(response.data));
    message.success(response.data.message);

    // Handle success modal
    yield put(setShowSuccessModal(true));

    // Call get recordings by month api
    const fetchCallRecordingsByMonthAction = {
      type: fetchCallRecordingsByMonth.type,
      payload: {
        agencyId,
        allocatorId,
        uploadYear,
      },
    };
    yield put(fetchCallRecordingsByMonthAction); // Dispatch the action
  } catch (error: any) {
    yield* handleError(error, uploadCallRecordingsFailure, '');
  }
}

function* handleFetchDistinctYearsSaga(
  action: PayloadAction<IGetDistinctYearsRequest>
) {
  try {
    const { allocatorId, agencyId } = action.payload;
    const endpoint = complianceEndPoints.GET_DISTINCT_YEARS(
      allocatorId,
      agencyId
    );
    const response: AxiosResponse<IGetDistinctYearsResponse> =
      yield HTTPClient.GetRequest(endpoint);
    yield put(fetchDistinctYearsSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchDistinctYearsFailure, '');
  }
}
export function* watchRecordingSaga() {
  yield takeLatest(
    fetchCallRecordingSummary.type,
    handleFetchCallRecordingSummarySaga
  );
  yield takeLatest(
    fetchAllCallRecordings.type,
    handleFetchAllCallRecordingsSaga
  );
  yield takeLatest(
    fetchCallRecordingsByMonth.type,
    handleFetchCallRecordingsByMonthSaga
  );
  yield takeLatest(downloadCallRecording.type, handleDownloadCallRecordingSaga);
  yield takeLatest(uploadCallRecordings.type, handleUploadCallRecordingsSaga);
  yield takeLatest(fetchDistinctYears.type, handleFetchDistinctYearsSaga);
}
